import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type TableName =
  | "transfer_airports_v2"
  | "transfer_zones"
  | "transfer_zone_prices"
  | "transfer_hotels_v2"
  | "transfer_service_types"
  | "transfer_km_rates"
  | "transfer_vehicles_v2"
  | "transfer_promo_codes"
  | "transfer_bookings_v2";

export const useV2List = (table: TableName, adminMode = false) => {
  return useQuery({
    queryKey: [table, adminMode ? "admin" : "public"],
    queryFn: async () => {
      let q: any = supabase.from(table as any).select("*");
      if (!adminMode && table !== "transfer_bookings_v2" && table !== "transfer_promo_codes") {
        q = q.eq("is_active", true);
      }
      const tablesWithSortOrder = new Set([
        "transfer_airports_v2",
        "transfer_hotels_v2",
        "transfer_service_types",
        "transfer_vehicles_v2",
        "transfer_zones",
      ]);
      if (tablesWithSortOrder.has(table)) {
        q = q.order("sort_order", { ascending: true, nullsFirst: false });
      }
      q = q.order("created_at", { ascending: false });
      const { data, error } = await q;
      if (error) throw error;
      return data as any[];
    },
  });
};

export const useV2Save = (table: TableName) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, values }: { id?: string; values: any }) => {
      if (id) {
        const { data, error } = await (supabase.from(table as any) as any).update(values).eq("id", id).select().single();
        if (error) throw error;
        return data;
      }
      const { data, error } = await (supabase.from(table as any) as any).insert(values).select().single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [table] });
      toast.success("Saved");
    },
    onError: (e: any) => toast.error(e.message || "Save failed"),
  });
};

export const useV2Delete = (table: TableName) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await (supabase.from(table as any) as any).delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [table] });
      toast.success("Deleted");
    },
    onError: (e: any) => toast.error(e.message || "Delete failed"),
  });
};

// Haversine distance between two lat/lng in km (fallback if Google Maps fails)
export const distanceKm = (a: { lat: number; lng: number }, b: { lat: number; lng: number }) => {
  const toRad = (v: number) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
};

// Real-world driving distance via Google Distance Matrix (server-side), using address names.
export const getDrivingDistanceKm = async (
  originAddress: string,
  destinationAddress: string,
): Promise<{ km: number; durationText?: string; source: "google" | "fallback" }> => {
  try {
    const { data, error } = await supabase.functions.invoke("google-distance", {
      body: { originAddress, destinationAddress },
    });
    if (error || !data?.distance_km) throw error ?? new Error("no distance");
    return { km: data.distance_km, durationText: data.duration_text, source: "google" };
  } catch (_e) {
    return { km: 0, source: "fallback" };
  }
};

export const calculatePrice = (
  km: number,
  rate: { base_fee: number; price_per_km: number; minimum_price: number; free_km: number },
  zoneMultiplier = 1,
  zoneBasePrice = 0
) => {
  const billableKm = Math.max(0, km - (rate.free_km || 0));
  const raw = (rate.base_fee || 0) + billableKm * (rate.price_per_km || 0) + (zoneBasePrice || 0);
  const withMultiplier = raw * (zoneMultiplier || 1);
  return Math.max(rate.minimum_price || 0, Math.round(withMultiplier * 100) / 100);
};

// Zone-based pricing (newlineshuttle style)
export const getZonePrice = (
  price: { base_price: number; max_pax: number; extra_per_pax: number } | null | undefined,
  pax: number
): number | null => {
  if (!price) return null;
  const extraPax = Math.max(0, (pax || 0) - (price.max_pax || 0));
  const total = (price.base_price || 0) + extraPax * (price.extra_per_pax || 0);
  return Math.round(total * 100) / 100;
};
